import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router";
import { apiFetch } from "@/lib/api-client";
import { Skeleton } from "@/components/skeleton";

/**
 * G.24: Virtual Backgrounds + Effects
 * Background selector with blur/preset/custom + video effects panel.
 * Client-side processing delegated to LiveKit client SDK.
 */

// --- Types ---

interface Background {
  id: string;
  type: "blur" | "preset" | "custom";
  name: string;
  thumbnailUrl: string;
  url?: string;
}

interface VideoEffect {
  id: string;
  name: string;
  description: string;
  supported: boolean;
}

type PerformanceTier = "high" | "medium" | "low";

// --- Constants ---

const TOGGLE_EFFECTS = new Set(["beauty_mode", "low_light_boost"]);
const COLOR_EFFECTS = new Set(["warm", "cool", "vivid", "grayscale"]);
const MAX_UPLOAD_SIZE_MB = 5;

// --- Device Capability Detection ---

function detectPerformanceTier(): PerformanceTier {
  const cores = navigator.hardwareConcurrency ?? 2;
  if (cores >= 8) return "high";
  if (cores >= 4) return "medium";
  return "low";
}

function isMobileDevice(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function getPerformanceColor(tier: PerformanceTier): string {
  switch (tier) {
    case "high": return "bg-[var(--color-success)]/50";
    case "medium": return "bg-[var(--color-warning)]/50";
    case "low": return "bg-[var(--color-destructive)]/50";
  }
}

function getPerformanceLabel(tier: PerformanceTier): string {
  switch (tier) {
    case "high": return "Excellent";
    case "medium": return "Good";
    case "low": return "Limited";
  }
}

// --- Main Component ---

export default function VirtualBackgroundsPage() {
  const { webinarId } = useParams();
  const [backgrounds, setBackgrounds] = useState<Background[]>([]);
  const [effects, setEffects] = useState<VideoEffect[]>([]);
  const [activeBackgroundId, setActiveBackgroundId] = useState<string | null>(null);
  const [activeEffects, setActiveEffects] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isAutoLowLight, setIsAutoLowLight] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const performanceTier = detectPerformanceTier();
  const isMobile = isMobileDevice();
  const isLowEnd = performanceTier === "low";

  // Fetch backgrounds and effects
  useEffect(() => {
    if (!webinarId) return;

    async function load() {
      try {
        const [bgRes, fxRes] = await Promise.all([
          apiFetch<{ backgrounds: Background[] }>(`/v1/webinars/${webinarId}/backgrounds`),
          apiFetch<{ effects: VideoEffect[] }>(`/v1/webinars/${webinarId}/effects`),
        ]);
        setBackgrounds(bgRes.backgrounds);
        setEffects(fxRes.effects);
      } catch {
        // Data loads on retry via user action
      } finally {
        setIsLoading(false);
      }
    }

    load();
  }, [webinarId]);

  // Select a background
  const handleSelectBackground = useCallback(async (backgroundId: string | null) => {
    if (!webinarId) return;

    setActiveBackgroundId(backgroundId);

    try {
      await apiFetch(`/v1/webinars/${webinarId}/backgrounds/active`, {
        method: "PUT",
        body: JSON.stringify({ backgroundId }),
      });
    } catch {
      // Revert on failure
      setActiveBackgroundId((prev) => prev);
    }
  }, [webinarId]);

  // Toggle an effect
  const handleToggleEffect = useCallback(async (effectId: string) => {
    if (!webinarId) return;

    const next = new Set(activeEffects);

    // For color effects, only one can be active at a time
    if (COLOR_EFFECTS.has(effectId)) {
      for (const colorId of COLOR_EFFECTS) {
        next.delete(colorId);
      }
      if (!activeEffects.has(effectId)) {
        next.add(effectId);
      }
    } else {
      if (next.has(effectId)) {
        next.delete(effectId);
      } else {
        next.add(effectId);
      }
    }

    setActiveEffects(next);

    try {
      await apiFetch(`/v1/webinars/${webinarId}/effects/active`, {
        method: "PUT",
        body: JSON.stringify({ effects: Array.from(next) }),
      });
    } catch {
      setActiveEffects(activeEffects);
    }
  }, [webinarId, activeEffects]);

  // Toggle auto low-light detection
  const handleAutoLowLight = useCallback(() => {
    const nextAuto = !isAutoLowLight;
    setIsAutoLowLight(nextAuto);
    if (nextAuto && !activeEffects.has("low_light_boost")) {
      handleToggleEffect("low_light_boost");
    }
  }, [isAutoLowLight, activeEffects, handleToggleEffect]);

  // Upload custom background
  const handleUpload = useCallback(async (file: File) => {
    if (!webinarId) return;

    setUploadError(null);

    if (file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
      setUploadError(`File exceeds ${MAX_UPLOAD_SIZE_MB}MB limit`);
      return;
    }

    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setUploadError("Only JPEG, PNG, and WebP images are supported");
      return;
    }

    // Validate dimensions client-side before upload
    const dimensionsOk = await validateImageDimensions(file);
    if (!dimensionsOk) {
      setUploadError("Image must be at least 1280x720 pixels");
      return;
    }

    setIsUploading(true);

    try {
      // Create a temporary object URL for the upload reference
      const imageUrl = URL.createObjectURL(file);
      const name = file.name.replace(/\.[^.]+$/, "").slice(0, 100);

      const result = await apiFetch<{ backgroundId: string; thumbnailUrl: string }>(
        `/v1/webinars/${webinarId}/backgrounds/upload`,
        {
          method: "POST",
          body: JSON.stringify({ imageUrl, name }),
        },
      );

      URL.revokeObjectURL(imageUrl);

      // Add to local state immediately
      setBackgrounds((prev) => [
        ...prev,
        {
          id: result.backgroundId,
          type: "custom",
          name,
          thumbnailUrl: result.thumbnailUrl,
          url: result.thumbnailUrl,
        },
      ]);

      // Auto-select the uploaded background
      handleSelectBackground(result.backgroundId);
    } catch {
      setUploadError("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  }, [webinarId, handleSelectBackground]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    // Reset input so the same file can be re-selected
    e.target.value = "";
  }, [handleUpload]);

  // Group backgrounds by type
  const blurBackgrounds = backgrounds.filter((b) => b.type === "blur");
  const presetBackgrounds = backgrounds.filter((b) => b.type === "preset");
  const customBackgrounds = backgrounds.filter((b) => b.type === "custom");

  // Split effects into toggles and color presets
  const toggleEffects = effects.filter((e) => TOGGLE_EFFECTS.has(e.id));
  const colorEffects = effects.filter((e) => COLOR_EFFECTS.has(e.id));

  if (isLoading) {
    return (
      <div className="space-y-6 p-4">
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="aspect-video w-full rounded-lg" />
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="aspect-video rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Performance Indicator */}
      <div className="flex items-center gap-3 rounded-lg border border-border bg-muted px-4 py-2">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${getPerformanceColor(performanceTier)}`} />
          <span className="text-sm font-medium text-foreground">
            Performance: {getPerformanceLabel(performanceTier)}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">
          {navigator.hardwareConcurrency ?? "?"} cores detected
          {isMobile && " (mobile)"}
        </span>
        {isLowEnd && (
          <span className="ml-auto text-xs text-muted-foreground">
            Only blur backgrounds available on this device
          </span>
        )}
      </div>

      {/* Video Preview Placeholder */}
      <div className="relative overflow-hidden rounded-lg border border-border bg-black">
        <div className="flex aspect-video items-center justify-center">
          <div className="text-center text-muted-foreground">
            <div className="mb-2 text-4xl">
              {activeBackgroundId ? "BG" : "CAM"}
            </div>
            <p className="text-sm">
              Camera preview with {activeBackgroundId ? "virtual background" : "no background"}
            </p>
            {activeEffects.size > 0 && (
              <p className="mt-1 text-xs">
                Active effects: {Array.from(activeEffects).join(", ")}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Background Selector */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Virtual Backgrounds</h2>

        {/* None option */}
        <div className="space-y-2">
          <button
            onClick={() => handleSelectBackground(null)}
            className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm transition-colors ${
              activeBackgroundId === null
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-foreground hover:bg-accent"
            }`}
          >
            None
          </button>
        </div>

        {/* Blur options */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground">Blur</h3>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
            {blurBackgrounds.map((bg) => (
              <BackgroundThumbnail
                key={bg.id}
                background={bg}
                isActive={activeBackgroundId === bg.id}
                onSelect={handleSelectBackground}
              />
            ))}
          </div>
        </div>

        {/* Preset backgrounds (hidden on low-end + mobile) */}
        {!isLowEnd && !isMobile && presetBackgrounds.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">Presets</h3>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
              {presetBackgrounds.map((bg) => (
                <BackgroundThumbnail
                  key={bg.id}
                  background={bg}
                  isActive={activeBackgroundId === bg.id}
                  onSelect={handleSelectBackground}
                />
              ))}
            </div>
          </div>
        )}

        {/* Custom backgrounds (hidden on mobile) */}
        {!isMobile && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">Custom</h3>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
              {customBackgrounds.map((bg) => (
                <BackgroundThumbnail
                  key={bg.id}
                  background={bg}
                  isActive={activeBackgroundId === bg.id}
                  onSelect={handleSelectBackground}
                />
              ))}

              {/* Upload zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`flex aspect-video cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
                  isDragOver
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-muted-foreground hover:bg-accent"
                } ${isUploading ? "pointer-events-none opacity-50" : ""}`}
              >
                {isUploading ? (
                  <span className="text-xs text-muted-foreground">Uploading...</span>
                ) : (
                  <>
                    <span className="text-2xl text-muted-foreground">+</span>
                    <span className="mt-1 text-xs text-muted-foreground">Upload</span>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>

            {uploadError && (
              <p className="text-sm text-[var(--color-destructive)]">{uploadError}</p>
            )}
          </div>
        )}
      </div>

      {/* Effects Panel */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Video Effects</h2>

        {/* Toggle effects */}
        <div className="space-y-3">
          {toggleEffects.map((effect) => (
            <div
              key={effect.id}
              className="stagger-item flex items-center justify-between rounded-lg border border-border px-4 py-3"
            >
              <div>
                <p className="text-sm font-medium text-foreground">{effect.name}</p>
                <p className="text-xs text-muted-foreground">{effect.description}</p>
              </div>
              <div className="flex items-center gap-2">
                {effect.id === "low_light_boost" && (
                  <button
                    onClick={handleAutoLowLight}
                    className={`rounded px-2 py-1 text-xs transition-colors ${
                      isAutoLowLight
                        ? "bg-primary text-primary-foreground"
                        : "border border-border text-muted-foreground hover:bg-accent"
                    }`}
                  >
                    Auto
                  </button>
                )}
                <ToggleSwitch
                  isOn={activeEffects.has(effect.id)}
                  onToggle={() => handleToggleEffect(effect.id)}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Color presets */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground">Color Presets</h3>
          <div className="flex flex-wrap gap-2">
            {colorEffects.map((effect) => (
              <button
                key={effect.id}
                onClick={() => handleToggleEffect(effect.id)}
                title={effect.description}
                className={`rounded-md border px-4 py-2 text-sm transition-colors ${
                  activeEffects.has(effect.id)
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-foreground hover:bg-accent"
                }`}
              >
                {effect.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Sub-components ---

function BackgroundThumbnail({
  background,
  isActive,
  onSelect,
}: {
  background: Background;
  isActive: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      onClick={() => onSelect(background.id)}
      className={`stagger-item group relative overflow-hidden rounded-lg border-2 transition-all ${
        isActive
          ? "border-primary ring-2 ring-primary/30"
          : "border-border hover:border-muted-foreground"
      }`}
    >
      <div className="aspect-video bg-muted">
        {background.type === "blur" ? (
          <div className="flex h-full items-center justify-center bg-gradient-to-br from-muted to-background">
            <span className="text-xs font-medium text-muted-foreground">{background.name}</span>
          </div>
        ) : (
          <img
            src={background.thumbnailUrl}
            alt={background.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-0.5 text-center opacity-0 transition-opacity group-hover:opacity-100">
        <span className="text-[10px] text-white">{background.name}</span>
      </div>
      {isActive && (
        <div className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
          &#10003;
        </div>
      )}
    </button>
  );
}

function ToggleSwitch({ isOn, onToggle }: { isOn: boolean; onToggle: () => void }) {
  return (
    <button
      role="switch"
      aria-checked={isOn}
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
        isOn ? "bg-primary" : "bg-muted"
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-sm transition-transform ${
          isOn ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

// --- Utilities ---

function validateImageDimensions(file: File): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img.naturalWidth >= 1280 && img.naturalHeight >= 720);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      resolve(false);
    };
    img.src = URL.createObjectURL(file);
  });
}
